import * as Sentry from '@sentry/browser';
import isEqual from 'lodash/isEqual';
import { v4 as uuidv4 } from 'uuid';
import {
    findAddOnById,
    hasAddOnId,
    isAddOnDraft,
    isAddOnPublished,
    normalizeAddOn,
} from '_shared_/UI/AddOns/addOnModel';
import {
    PENDING_ADD_ON_ID,
    isPendingAnchor,
    pageAnchorFor,
    pointAnchorFromPageClick,
    textAnchorFromHighlight,
} from '_shared_/UI/AddOns/addOnAnchorFromSelection';
import { CAPTURED_ANCHOR_KINDS } from '_shared_/UI/AddOns/addOnAnchorKinds.js';
import { nearestScale, nextScale } from '_shared_/UI/AddOns/addOnFontScale';
import { TEXT_FIELDS, draftFromAddOn, emptyAddOnDraft, recordFromDraft } from '_shared_/UI/AddOns/addOnDraft';
import { UnreadableAddOnImage, reduceAddOnImage } from '_shared_/UI/AddOns/addOnImage';
import { inferTypeFrom } from '_shared_/UI/AddOns/addOnType';
import { filterAddOns } from '_shared_/UI/AddOns/addOnSearch.js';
import { addOnPageSentence, addOnStrings } from '_shared_/UI/AddOns/addOnStrings.js';
import { announceToScreenReader } from '_shared_/js/utils/screenReaderAnnounce.js';
import { AddOnRefused, addOnsAPI } from '../communication/addOns';
import { hostRecord, localRecord, withLocalImage } from './addOnRecords';
import { addOnWriteQueueOf } from './addOnWriteQueue';

/**
 * Spread into the root store rather than namespaced: the state this slice
 * reads (`articles`, `sharedArticle`, `initialPanel`, `ui.addOnsPanel`,
 * `ui.features.addOns`) lives at the root, and every consumer addresses
 * these names there.
 */

/**
 * What a navigation carries when `addOnEditorHoldsPageTurn` has nothing left
 * to ask: the operator has already answered the question, `goToNextPage` or
 * `goToPrevPage` asked it before they animated, or nobody turned the page at
 * all. It reaches no host and no analytics, since none of the four turns
 * forwards the payload it was given anywhere.
 */
export const ADD_ON_EDITOR_CONFIRMED = 'addOnEditorConfirmed';

/**
 * The four actions a page turn goes through, and so the only ones to replay.
 * The arrows are here as well as the two they end in, because the question has
 * to be asked before they animate and before they report the end of the book.
 */
const REPLAYABLE_TURNS = ['goToPage', 'goToLocation', 'goToNextPage', 'goToPrevPage'];

/**
 * The anchor a capture starts from: the kind being asked for, on the page the
 * operator is standing on. The page is where they are, not where the piece
 * will sit, which is why nothing reads it until the capture lands.
 *
 * @param {string} kind - The anchor kind the page is being asked for
 * @param {object} getters
 * @returns {object}
 */
const captureOn = (kind, getters) => ({ type: kind, page: getters.currentPageAnchor.page, pending: true });

/**
 * A figure on the page the reader is on, which only the EPUB frames can resolve.
 *
 * @param {object} getters
 * @param {{ src: string, alt?: string, cssSelector?: string }} image
 * @returns {object}
 */
const imageAnchorOn = (getters, image) => ({ ...getters.currentPageAnchor, type: 'image', image });

/** The modal takes plain strings, so the page is in the sentence before it is committed. */
const withPage = (text, page) => (text ?? '').replace('{page}', page);

/** Is `editor` still the form open? The host can answer once the operator has moved to another one. */
const ownsForm = (state, editor) => state.ui.addOnsPanel.editor === editor;

/**
 * Holds a form's buttons while a write the operator asked for waits for the
 * host. A form opened since keeps its own.
 */
function busyFlag({ state, commit }, editor) {
    return busy => {
        if (ownsForm(state, editor)) commit('setAddOnEditorBusy', busy);
    };
}

/**
 * Hands the store the record a form built and leaves the form as it is:
 * the record as stored, or null with `titleError`, `imageError` or
 * `saveError` saying why.
 * A form on a stored piece sends only what it changed since the copy it
 * knows the host holds, so a form opened while another save of the piece
 * was on its way never sends back the values it opened with.
 *
 * A new piece's form keeps the record of every save the host refused. The
 * host may have stored any of them, and it answers a repeated uuid with the
 * piece it stored and changes nothing, so once a save is answered, what the
 * record changed since those tries follows as an update of that piece.
 *
 * @param {{ state: object, commit: Function, dispatch: Function }} context
 * @param {object} record - What `recordFromDraft` built from the form, less any field this save leaves out
 * @param {object} editor - The form the record is from
 * @returns {Promise<object|null>}
 */
async function saveEditorRecord({ state, commit, dispatch }, record, editor) {
    if (!record.title) {
        if (ownsForm(state, editor)) commit('setAddOnEditorTitleError', true);
        return null;
    }

    const sent = editor.baseline
        ? { id: record.id, ...changedFields(record, editor.baseline) }
        : { ...record, uuid: editor.uuid };
    if (ownsForm(state, editor)) {
        commit('setAddOnEditorSaveError', false);
        // Cleared first, so a retry that fails on the same file says so again.
        if (record.imageFile) commit('setAddOnEditorImageError', false);
    }
    try {
        const saved = await dispatch('saveAddOn', sent);
        // Also for a form gone meanwhile: its closing save, queued behind
        // this one, sends what differs from the copy the host now holds.
        if (editor.baseline) commit('setAddOnEditorBaseline', { editor, baseline: { ...editor.baseline, ...sent } });

        const unsent = changedSinceTries(record, editor.refusedTries);
        if (Object.keys(unsent).length === 0) return saved;

        return await dispatch('saveAddOn', { id: saved.id, ...unsent });
    } catch (error) {
        // A file the browser cannot read never reached the host, so only its field hears of it.
        if (error instanceof UnreadableAddOnImage) {
            if (ownsForm(state, editor)) commit('setAddOnEditorImageError', true);
            return null;
        }

        // The piece went away under the form, or the host refused the
        // record. Closing would take the writing with it. A refusal is the
        // operator's to read and fix, not a failure to report.
        const refused = error instanceof AddOnRefused;
        if (!refused) Sentry.captureException(error, { tags: { feature: 'add-ons' } });
        if (ownsForm(state, editor)) {
            commit('setAddOnEditorSaveError', refused ? error.message : true);
            if (record.id == null) commit('keepAddOnEditorRefusedTry', record);
        }
        return null;
    }
}

// What the reader writes of a piece. The host derives the page range and
// the legacy point from the anchor.
const WRITTEN_FIELDS = [...TEXT_FIELDS, 'imageFile', 'status', 'type', 'anchor'];

// The host and the editor spell the same markup differently: a hard space
// as the character or as &nbsp;, an accent in an attribute as an entity or
// as the character. The browser's parser reads both to one document.
const canonicalHtml = html => new DOMParser().parseFromString(html ?? '', 'text/html').body.innerHTML;

const sameValue = (field, left, right) =>
    field === 'body' ? canonicalHtml(left) === canonicalHtml(right) : isEqual(left ?? null, right ?? null);

/**
 * The fields a save changes on a stored piece. The host stores the image
 * and re-derives the page range on every write that carries them, so what
 * the form left alone stays out of the request.
 */
function changedFields(record, stored) {
    return Object.fromEntries(
        WRITTEN_FIELDS.filter(field => field in record && !sameValue(field, record[field], stored[field])).map(
            field => [field, record[field]]
        )
    );
}

/**
 * Every try is merged in because the host may hold any of them, and nothing says which.
 * A file the record no longer holds leaves the host's copy standing unless the image is named again.
 */
const changedSinceTries = (record, tries) => ({
    ...Object.assign({}, ...tries.map(tried => changedFields(record, tried))),
    ...(tries.some(tried => tried.imageFile) && 'imageFile' in record && !record.imageFile
        ? { imageUrl: record.imageUrl }
        : {}),
});

/**
 * @throws {UnreadableAddOnImage} When the browser cannot read the file
 */
async function withHostSizedImage(record) {
    if (!record.imageFile) return record;

    return { ...record, imageFile: await reduceAddOnImage(record.imageFile) };
}

/**
 * What a draft's form saves: the piece where it was while a capture asks the
 * page for a new place. Never the status, which only a caller that publishes
 * names: a draft's own save naming its old one would take back a publish it raced.
 */
function draftRecord({ state, getters }) {
    if (!getters.addOnEditorAutosaves) return null;

    const { draft, id, anchorBeforeCapture } = state.ui.addOnsPanel.editor;
    const anchor = isPendingAnchor(draft.anchor) ? anchorBeforeCapture : draft.anchor;
    const { status: _status, ...record } = recordFromDraft({ ...draft, anchor }, id, getters.addOnEditorType);

    return record;
}

/**
 * A change's save: the draft as it stands when the save goes, less its
 * image. A form that has gone sent its last change on its way out.
 */
async function saveChange(context, editor) {
    const record = ownsForm(context.state, editor) ? draftRecord(context) : null;
    if (!record) return null;

    // The host stores the image on every write that carries one, a URL it
    // fetches or a file, so the image goes with the save that closes the form.
    const { imageUrl: _imageUrl, imageFile: _imageFile, ...withoutImage } = record;
    return saveEditorRecord(context, withoutImage, editor);
}

/**
 * A page turn while a form is open moves the draft's anchor, so it asks
 * first. Answers true when the question is up and the navigation must not run.
 *
 * Nothing is asked while the page is already being asked for a place: a
 * pending anchor has nothing to lose and follows the reader wherever it lands.
 *
 * @param {{ state: object, getters: object, commit: Function }} context
 * @param {{ action: string, payload: object }} navigation - The turn to replay on Go
 * @returns {boolean}
 */
export function addOnEditorHoldsPageTurn({ state, getters, commit }, { action, payload }) {
    const anchor = state.ui?.addOnsPanel?.editor?.draft.anchor;
    if (!anchor || isPendingAnchor(anchor) || payload?.[ADD_ON_EDITOR_CONFIRMED]) return false;

    const strings = addOnStrings(state.localizations, state.lang);
    const page = String(getters.currentPage ?? '');
    // A passage costs the operator the work of picking it again; the other
    // kinds only move, a point keeping its spot, so they are not warned about
    // the same way.
    const message = anchor.type === 'text' ? strings.leavePagePlaceMessage : strings.leavePageMessage;

    commit('openConfirmationModal', {
        title: withPage(strings.leavePageTitle, page),
        message: withPage(message, page),
        confirmLabel: strings.leavePageGo,
        cancelLabel: withPage(strings.leavePageStay, page),
        onConfirmAction: 'addOns:leavePage',
        context: { action, payload },
        destructive: false,
    });

    return true;
}

export const addOnsGetters = {
    /**
     * The host sends every paginated content an add-ons list, empty or
     * not, and the platform gates nothing behind a feature: any tenant
     * admin may add pieces to any PDF. So a reader is offered the panel
     * when there is something to read in it, and an editor whenever they
     * may add the first piece.
     */
    canUseAddOns(state, getters) {
        return getters.visibleAddOns.length > 0 || getters.canEditAddOns;
    },
    /**
     * What this reader may see: everything for an editor, only what is
     * published for anyone else. Every surface lists from here and
     * never from `articles`, so a draft cannot leak through a
     * badge or a link.
     *
     * Content without pages (a reflowable EPUB the platform has not numbered,
     * an audiobook) has nothing to show and nowhere to anchor, so the list
     * is empty there and every surface reading it goes quiet at once.
     */
    visibleAddOns(state, getters) {
        if (!getters.hasPaginatedContent) return [];
        if (getters.canEditAddOns) return state.articles;
        return state.articles.filter(isAddOnPublished);
    },
    /** The piece the panel is reading, if the reader may see it. */
    addOnBeingRead(state, getters) {
        const { addOnId } = state.ui.addOnsPanel;
        if (addOnId == null) return null;

        return findAddOnById(getters.visibleAddOns, addOnId);
    },
    /**
     * The add-ons whose markers render open: the one being read and the
     * one a list row is pointing at. Both are the panel's own state, so a
     * closed panel emphasizes nothing — another feature can close it out
     * from under a hovered row, which clears neither.
     */
    emphasizedAddOnIds(state) {
        const { opened, addOnId, highlightedId } = state.ui.addOnsPanel;
        if (!opened) return new Set();

        return new Set([addOnId, highlightedId].filter(id => id != null).map(String));
    },
    /**
     * The add-ons in reading order — by page, the unplaced ones last —
     * shared by the list and the previous/next buttons, so "next" is
     * always the row below.
     */
    orderedAddOns(state, getters) {
        const pageOf = addOn => addOn.anchor?.page ?? Infinity;
        return [...getters.visibleAddOns].sort((a, b) => pageOf(a) - pageOf(b));
    },
    /**
     * The add-ons the list shows: the whole order, or the pieces the
     * reader's search finds in it.
     */
    listedAddOns(state, getters) {
        return filterAddOns(getters.orderedAddOns, state.ui.addOnsPanel.search);
    },
    /**
     * Where the piece being read sits among the listed ones, and its
     * neighbours — after a search, "next" is the next hit. A piece the
     * search leaves out (opened from a marker) has none.
     * @returns {{ position: number, total: number, previous: object|null, next: object|null }|null}
     */
    addOnNavigation(state, getters) {
        const { addOnId } = state.ui.addOnsPanel;
        if (addOnId == null) return null;

        const listed = getters.listedAddOns;
        const index = listed.findIndex(addOn => hasAddOnId(addOn, addOnId));
        if (index < 0) return null;

        return {
            position: index + 1,
            total: listed.length,
            previous: listed[index - 1] ?? null,
            next: listed[index + 1] ?? null,
        };
    },
    addOnBeingEdited(state) {
        return state.ui.addOnsPanel.editor;
    },
    /** The actions' `ownsForm`, for a component that read the form before awaiting a write. */
    ownsAddOnForm(state) {
        return editor => ownsForm(state, editor);
    },
    /**
     * Does the form's primary action publish? A piece nobody has stored yet
     * and one still held back both go out with it. Anything already
     * published only takes the edit.
     */
    shouldAddOnEditorPublish(state) {
        const editor = state.ui.addOnsPanel.editor;
        if (!editor) return false;

        return editor.id == null || isAddOnDraft(editor.draft);
    },
    /**
     * Does the form save every change as it is made? Only a stored draft's
     * does, which leaves it nothing to cancel. A published piece waits for
     * its Save, and a new one is saved once, published or as a draft.
     */
    addOnEditorAutosaves(state) {
        const editor = state.ui.addOnsPanel.editor;

        return editor?.id != null && isAddOnDraft(editor.draft);
    },
    /** What the automatic icon reads off the draft, which the picker offers even while it is off. */
    addOnEditorInferredType(state) {
        const editor = state.ui.addOnsPanel.editor;
        return editor ? inferTypeFrom(editor.draft) : null;
    },
    /** The icon the piece will carry: the one picked, or the one its content asks for. */
    addOnEditorType(state, getters) {
        const editor = state.ui.addOnsPanel.editor;
        if (!editor) return null;

        return editor.draft.typeAuto ? getters.addOnEditorInferredType : editor.draft.type;
    },
    /**
     * The draft as the marker hosts can draw it, so the icon is on the page
     * before the piece exists. Nothing while the anchor is still being
     * chosen: there is no place to draw it yet.
     */
    pendingAddOn(state, getters) {
        const editor = state.ui.addOnsPanel.editor;
        if (!editor || isPendingAnchor(editor.draft.anchor)) return null;

        return { ...recordFromDraft(editor.draft, PENDING_ADD_ON_ID, getters.addOnEditorType), pending: true };
    },
    /**
     * The pieces the page draws. The draft rides along with the saved ones so
     * its icon is on the page before it exists, which is how the operator sees
     * where they are putting it. One array for every host on screen: each of
     * them building its own would hand the others a new list to react to.
     */
    renderedAddOns(state, getters) {
        const { pendingAddOn, visibleAddOns } = getters;
        if (!pendingAddOn) return visibleAddOns;

        // A stored piece is drawn once, as its draft: its saved marker would sit
        // under the draft's, or stay where the piece no longer goes.
        const editedId = getters.addOnBeingEdited?.id;

        return [...visibleAddOns.filter(addOn => !hasAddOnId(addOn, editedId)), pendingAddOn];
    },
    /** Is the form waiting for the operator to point at the page? */
    addOnEditorAwaitsAnchor(state) {
        return isPendingAnchor(state.ui.addOnsPanel.editor?.draft.anchor);
    },
    /** The kind of anchor the form holds, which decides what the page is being asked for. */
    addOnEditorAnchorKind(state) {
        return state.ui.addOnsPanel.editor?.draft.anchor?.type ?? null;
    },
    /**
     * Where a piece created from the panel belongs: the page on screen.
     * A marked EPUB names the chapter too, which the host demands to
     * place a page anchor.
     */
    currentPageAnchor(state, getters) {
        return pageAnchorFor({
            page: getters.currentPage,
            href: getters.isEpub ? state.lastLocation.epub.chapterHref : undefined,
        });
    },
    /** A point only keeps its place on a page whose geometry is fixed. */
    canPlacePoint(state, getters) {
        return getters.isPdf || (getters.isEpub && state.isFixedLayout);
    },
    /**
     * May this reader write pieces? The host's word, a named session
     * (an anonymous one has nowhere to save, the rule bookmarks and
     * flashcards follow) and content with pages to anchor them to, never
     * in a preview, which offers no add-ons at all. The book's pagination
     * arrives long after the session does.
     */
    canEditAddOns(state, getters) {
        return (
            state.ui.features.addOns.editing &&
            !state.anonymousSession &&
            !state.previewMode &&
            getters.hasPaginatedContent
        );
    },
};

/**
 * Whatever is stored as a draft is "saved as a draft", a new piece included.
 * Any other piece reaching readers for the first time is "published", whatever
 * button did it.
 */
function savedAnnouncement(strings, publishes, saved) {
    if (isAddOnDraft(saved)) return strings.announceDraftSaved;
    if (publishes) return strings.announcePublished;

    return strings.announceSaved;
}

export const addOnsActions = {
    /**
     * Header, close button and Escape all land on the whole list: closing
     * forgets the piece that was open and the search, and opening never
     * resumes them.
     */
    toggleAddOnsPanel({ getters, commit, dispatch }) {
        commit('closeAllSettingsOrFeatures', { featureKey: 'addOnsPanel' });
        commit('toggleFeature', { featureKey: 'addOnsPanel' });
        commit('setAddOnViewer', null);
        // A draft keeps its last change when its form goes with the panel.
        if (getters.addOnEditorAutosaves) dispatch('flushAddOnAutosave');
        commit('closeAddOnEditor');
        commit('setAddOnSearch', '');
        commit('setAddOnFontScale', 1);
    },
    stepAddOnFontScale({ state, commit }, direction) {
        commit('setAddOnFontScale', nextScale(state.ui.addOnsPanel.fontScale, direction));
    },
    /**
     * The one way an add-on opens, whatever pointed at it: a marker, a
     * badge, a list row or a shared link. Opening does not navigate: a
     * marker is on screen already, and a list row knows where its piece is.
     * @param {string|number} addOnId
     */
    openAddOn({ state, getters, commit, dispatch }, addOnId) {
        if (!findAddOnById(getters.visibleAddOns, addOnId)) return;

        // Opening from the page starts a fresh panel: beside the page, and
        // with the list behind the piece no longer narrowed by a search from
        // the last time it was open. Opening from inside the panel keeps
        // both — the search is the reader's own and 'back to list' owes them
        // the results, and the layout is the one they are reading in.
        if (!state.ui.addOnsPanel.opened) {
            commit('setAddOnSearch', '');
            commit('setAddOnViewerFullscreen', false);
        }

        commit('closeAllSettingsOrFeatures', { featureKey: 'addOnsPanel' });
        commit('openFeature', { featureKey: 'addOnsPanel' });
        // Reading a piece and writing one are the same slot, so opening one
        // drops the other: a marker tapped mid-edit reads the tapped piece.
        // A draft keeps its last change all the same.
        if (getters.addOnEditorAutosaves) dispatch('flushAddOnAutosave');
        commit('closeAddOnEditor');
        commit('setAddOnViewer', addOnId);
        dispatch('trackEvent', {
            action: 'AddOn - open',
            trigger: '',
            page: state.reader.pagination.current,
            source: 'direct',
        });
    },
    /**
     * Choosing a piece from the panel: opens it and turns to it. PDF
     * navigates by page number. EPUB navigates by the anchor's locator: a
     * full one lands on the position, a printed page is placed from the
     * book's page markers, and a bare href lands on the chapter. In a
     * fixed-layout book a position is a page, so a bare page number lands
     * on it. A page anchor in a reflowable book without printed pages only
     * opens the piece: positions move with the font size and are no page.
     */
    goToAddOn({ state, getters, dispatch }, addOnId) {
        const addOn = findAddOnById(getters.visibleAddOns, addOnId);
        if (!addOn) return;

        dispatch('openAddOn', addOn.id);

        const { anchor } = addOn;
        if (!anchor) return;

        if (!getters.isEpub) {
            if (anchor.page) dispatch('goToPage', { page: anchor.page, trigger: 'click', source: 'add-ons' });
            return;
        }

        const { locator } = anchor;
        if (locator?.locations) {
            dispatch('goToLocation', { epubR2Location: locator });
            return;
        }

        const printedPage =
            anchor.page && getters.epubUsesPrintedPages
                ? state.epubViewer?.printedPageLocator(anchor.page, {
                      spine: state.epub.spine,
                      totalPages: state.reader.pagination.total,
                  })
                : null;
        if (printedPage) {
            dispatch('goToLocation', { epubPrintedPageLocation: printedPage });
            return;
        }

        if (locator?.href) {
            dispatch('goToLocation', { chapterHref: locator.href });
            return;
        }

        if (anchor.page && state.isFixedLayout) {
            dispatch('goToLocation', { epubR2PositionIndex: anchor.page - 1 });
        }
    },
    /**
     * `?add_on_id=`: turns the publication to the piece the link names and
     * opens it, once, as soon as the reader can navigate.
     *
     * It answers on readiness rather than on the panel, which only exists
     * once the publication has add-ons — the very thing the link waits for.
     */
    openSharedAddOn({ state, getters, commit, dispatch }) {
        const { id } = state.sharedArticle;
        if (!id) return;
        // The link can only carry text; the PDF payload numbers its ids.
        if (!getters.visibleAddOns.some(addOn => hasAddOnId(addOn, id))) return;

        commit('setSharedArticle', { articleId: '' });
        dispatch('goToAddOn', id);
    },
    /**
     * The host's `initialPanel`: opens the panel the toolbar button opens,
     * once, as soon as the reader can show it. Answering it forgets it, so
     * a panel the reader closes stays closed.
     */
    openInitialPanel({ state, getters, commit, dispatch }) {
        if (state.initialPanel !== 'addOns') return;

        commit('setInitialPanel', null);
        if (!getters.canUseAddOns || state.ui.addOnsPanel.opened) return;

        dispatch('toggleAddOnsPanel');
    },
    closeAddOnViewer({ commit }) {
        commit('setAddOnViewer', null);
    },
    toggleAddOnViewerFullscreen({ state, commit }) {
        commit('setAddOnViewerFullscreen', !state.ui.addOnsPanel.fullscreen);
    },
    /**
     * A new piece, anchored where the reader is unless the caller places it.
     * @param {{ anchor?: object }} [payload]
     */
    createAddOn({ state, getters, commit, dispatch }, { anchor } = {}) {
        if (!getters.canEditAddOns) return;

        // Same rule openAddOn follows: a panel opened from the page starts
        // fresh, so the list behind the form is not still narrowed by a
        // search from the last time it was open.
        if (!state.ui.addOnsPanel.opened) commit('setAddOnSearch', '');

        commit('closeAllSettingsOrFeatures', { featureKey: 'addOnsPanel' });
        commit('openFeature', { featureKey: 'addOnsPanel' });
        commit('setAddOnViewer', null);
        // A draft the new piece replaces keeps its last change.
        if (getters.addOnEditorAutosaves) dispatch('flushAddOnAutosave');
        commit('openAddOnEditor', {
            id: null,
            draft: emptyAddOnDraft({ anchor: anchor ?? getters.currentPageAnchor }),
            uuid: uuidv4(),
        });
    },
    /**
     * Edits a piece the reader can already see, so a draft only opens where
     * the visibility gate has let it through.
     * @param {string|number} addOnId
     */
    editAddOn({ getters, commit }, addOnId) {
        const addOn = findAddOnById(getters.visibleAddOns, addOnId);
        if (!addOn) return;

        const draft = draftFromAddOn(addOn);
        commit('openAddOnEditor', { id: addOn.id, draft, baseline: recordFromDraft(draft, addOn.id, draft.type) });
    },
    /**
     * A new piece on a figure, which only the EPUB frames can resolve.
     * @param {{ src: string, alt?: string, cssSelector?: string }} image
     */
    createAddOnForImage({ getters, dispatch }, image) {
        dispatch('createAddOn', { anchor: imageAnchorOn(getters, image) });
    },
    /**
     * The figure's + with a form open: pressing it is the deliberate move
     * that re-anchors the piece being written, whatever it was waiting for.
     * @param {{ src: string, alt?: string, cssSelector?: string }} image
     */
    anchorAddOnEditorToImage({ state, getters, commit }, image) {
        if (!state.ui.addOnsPanel.editor) return;

        commit('setAddOnEditorAnchor', imageAnchorOn(getters, image));
    },
    /**
     * The page is the editor's input: text and point wait for it, the page
     * needs no waiting.
     * @param {'page'|'text'|'point'} kind
     */
    setAddOnEditorAnchorKind({ state, getters, commit }, kind) {
        if (!state.ui.addOnsPanel.editor) return;

        if (kind === 'page') {
            commit('setAddOnEditorAnchor', getters.currentPageAnchor);
            return;
        }
        if (kind === 'point' && !getters.canPlacePoint) return;

        commit('startAddOnEditorAnchorCapture', captureOn(kind, getters));
    },
    /**
     * Asks the page again for the place of the anchor the draft already has:
     * the re-anchor button. Only a passage and a point are the operator's to
     * point at, so the other kinds have nothing to ask for.
     */
    recaptureAddOnEditorAnchor({ state, getters, commit }) {
        const anchor = state.ui.addOnsPanel.editor?.draft.anchor;
        if (!CAPTURED_ANCHOR_KINDS.includes(anchor?.type) || isPendingAnchor(anchor)) return;

        commit('startAddOnEditorAnchorCapture', captureOn(anchor.type, getters));
    },
    /**
     * Leaves the capture with the anchor the draft came in with. A piece whose
     * first anchor was already pending has none to come back to, so it lands
     * on the page on screen rather than on nothing.
     */
    cancelAddOnEditorAnchorCapture({ state, getters, commit }) {
        const editor = state.ui.addOnsPanel.editor;
        if (!editor || !isPendingAnchor(editor.draft.anchor)) return;

        commit('setAddOnEditorAnchor', editor.anchorBeforeCapture ?? getters.currentPageAnchor);
    },
    /**
     * The turn the operator answered Go to. The navigation runs again, past
     * the question this time, and the draft's anchor goes with it: a passage
     * is asked for again on the page it reaches, and every other kind lands
     * on that page itself.
     *
     * @param {{ action: string, payload: object }} navigation - What the question held back
     */
    leavePageWithAddOnEditor({ state, dispatch }, { action, payload } = {}) {
        const anchor = state.ui.addOnsPanel.editor?.draft.anchor;
        if (!REPLAYABLE_TURNS.includes(action) || !anchor || isPendingAnchor(anchor)) return;

        dispatch(action, { ...payload, [ADD_ON_EDITOR_CONFIRMED]: true });

        // Asking the new page for the passage is the re-anchor button's job,
        // so the turn borrows it rather than owning a second copy.
        if (anchor.type === 'text') {
            dispatch('recaptureAddOnEditorAnchor');
            return;
        }

        dispatch('anchorAddOnEditorToCurrentPage');
    },
    /**
     * Lands the draft on the page the reader is on. A PDF is already there by
     * the time the turn returns; an EPUB navigator arrives afterwards, so the
     * panel asks again once the reader reports where the turn ended.
     *
     * A point keeps its spot: the operator sees where it landed and moves it
     * from the re-anchor button, rather than being made to place it before
     * anything else. A passage cannot be carried over, so it is left to the
     * capture that asks for it.
     */
    anchorAddOnEditorToCurrentPage({ state, getters, commit }) {
        const anchor = state.ui.addOnsPanel.editor?.draft.anchor;
        if (!anchor || isPendingAnchor(anchor) || anchor.type === 'text') return;

        const landed = getters.currentPageAnchor;
        commit(
            'setAddOnEditorAnchor',
            anchor.type === 'point' ? { ...landed, type: 'point', point: anchor.point } : landed
        );
    },
    /**
     * The page's answer to a draft that asked for one. Moving an anchor is a
     * deliberate press, so a proposal that arrives while the draft is not
     * waiting is the reader reading, and nothing happens.
     */
    proposeAddOnEditorAnchor({ state, commit }, anchor) {
        const editor = state.ui.addOnsPanel.editor;
        if (!editor || !isPendingAnchor(editor.draft.anchor)) return;

        commit('setAddOnEditorAnchor', anchor);
    },
    /** What the reader just selected, offered to a form waiting for a passage. */
    anchorAddOnToSelection({ state, getters, dispatch }) {
        const draftAnchor = state.ui.addOnsPanel.editor?.draft.anchor;
        if (draftAnchor?.type !== 'text' || !isPendingAnchor(draftAnchor)) return;

        const anchor = textAnchorFromHighlight(state.currentHighlight, {
            isEpub: getters.isEpub,
            page: getters.currentPage,
        });
        if (anchor) dispatch('proposeAddOnEditorAnchor', anchor);
    },
    placeAddOnPoint({ state, getters, dispatch }, click) {
        const draftAnchor = state.ui.addOnsPanel.editor?.draft.anchor;
        if (draftAnchor?.type !== 'point' || !isPendingAnchor(draftAnchor) || !getters.canPlacePoint) return;

        dispatch('proposeAddOnEditorAnchor', pointAnchorFromPageClick(click));
        const strings = addOnStrings(state.localizations, state.lang);
        announceToScreenReader(
            addOnPageSentence(strings.announcePointPlacedNoPage, strings.announcePointPlaced, click.page)
        );
    },
    /**
     * Saves the form: a record means saved, null means the form stays open,
     * and `titleError`, `imageError` or `saveError` says which refusal kept
     * it there. A draft whose anchor the page is still being asked for is
     * held with none of them, since the form's buttons already wait for the
     * page. So is a new piece while the host has yet to answer its last save,
     * which would store it twice. The save waits for the writes asked for
     * before it, and drops a draft's change still waiting for its second,
     * which it sends anyway.
     * @param {{ status?: string }} [options] - The status the piece is saved
     *   with. The form's own stands otherwise, so a draft is published only
     *   by a caller that names it.
     * @returns {Promise<object|null>} The record as stored, or null
     */
    async saveAddOnEditor(context, { status } = {}) {
        const { state, getters, commit } = context;
        const editor = state.ui.addOnsPanel.editor;
        // Read when the save is asked for: the editor this save is for, not
        // whatever the panel holds by the time it goes.
        const publishes = getters.shouldAddOnEditorPublish;
        if (!editor || isPendingAnchor(editor.draft.anchor)) return null;

        const writes = addOnWriteQueueOf(state);
        if (editor.id == null && writes.busy(editor)) return null;

        writes.dropAutosave();
        return writes.write(
            editor,
            async () => {
                // The wait for the writes ahead leaves room to open another form or to start a capture.
                if (!ownsForm(state, editor) || isPendingAnchor(editor.draft.anchor)) return null;

                const record = recordFromDraft(editor.draft, editor.id, getters.addOnEditorType, { status });
                const saved = await saveEditorRecord(context, record, editor);
                if (!saved) return null;

                announceToScreenReader(
                    savedAnnouncement(addOnStrings(state.localizations, state.lang), publishes, saved)
                );
                if (ownsForm(state, editor)) {
                    commit('closeAddOnEditor');
                    commit('setAddOnViewer', saved.id);
                }

                return saved;
            },
            busyFlag(context, editor)
        );
    },
    /** Schedules the save of a form that saves every change. Any other form waits for its Save. */
    autosaveAddOnEditor(context) {
        const { state, getters } = context;
        if (!getters.addOnEditorAutosaves) return;

        const editor = state.ui.addOnsPanel.editor;
        addOnWriteQueueOf(state).autosave(editor, () => saveChange(context, editor));
    },
    /**
     * Saves the draft as it stands, image included, once the writes asked
     * for before it are in. A record is taken now, so a form that closes
     * meanwhile still sends its last state. A form still open sends the
     * draft as it stands when the save goes, and again for any change made
     * while that save travels.
     * @returns {Promise<object|null>} The last record as stored, or null
     */
    async flushAddOnAutosave(context) {
        const { state } = context;
        const editor = state.ui.addOnsPanel.editor;
        const before = draftRecord(context);
        if (!before) return null;

        const writes = addOnWriteQueueOf(state);
        writes.dropAutosave();
        return writes.writeDraft(
            editor,
            async () => {
                // A delete asked for before this one leaves nothing to save.
                if (!findAddOnById(state.articles, editor.id)) return null;

                // A form opened meanwhile is another piece of writing, which its own saves send.
                const record = ownsForm(state, editor) ? (draftRecord(context) ?? before) : before;
                return saveEditorRecord(context, record, editor);
            },
            busyFlag(context, editor)
        );
    },
    /**
     * A draft's way out of its form: back to the piece, once its last change
     * is saved. A change the store refuses keeps the form open, since
     * a draft has no Cancel to drop it with.
     * @returns {Promise<boolean>} Whether the form closed
     */
    async leaveAddOnDraft({ state, commit, dispatch }) {
        const editor = state.ui.addOnsPanel.editor;
        const saved = await dispatch('flushAddOnAutosave');
        // The save can answer once the operator has gone somewhere else, which it leaves alone.
        if (!saved || !ownsForm(state, editor)) return false;

        commit('closeAddOnEditor');
        commit('setAddOnViewer', saved.id);

        return true;
    },
    /** Leaves the form: back to the piece it was editing, or to the list for a new one. */
    cancelAddOnEditor({ state, getters, commit, dispatch }) {
        // A draft has nothing to cancel: the panel closing under it keeps its
        // last change, whether that change is waiting for its timer or for a
        // save on its way. A draft with nothing new sends nothing.
        if (getters.addOnEditorAutosaves) dispatch('flushAddOnAutosave');

        const id = state.ui.addOnsPanel.editor?.id ?? null;
        commit('closeAddOnEditor');
        commit('setAddOnViewer', id);
    },
    /**
     * Deletes the piece the form holds, once the writes asked for before it
     * are in, and drops a draft's change still waiting for its second. A
     * refused delete keeps the form open with `deleteError` saying so. The
     * host can answer after the operator has moved to another piece's form,
     * which the answer leaves alone.
     * @returns {Promise<boolean>} Whether the piece is gone
     */
    async deleteAddOnFromEditor(context) {
        const { state, commit, dispatch } = context;
        const editor = state.ui.addOnsPanel.editor;
        const id = editor?.id;
        if (id == null) return false;

        const writes = addOnWriteQueueOf(state);
        writes.dropAutosave();
        return writes.write(
            editor,
            async () => {
                const formHoldsPiece = () => state.ui.addOnsPanel.editor?.id === id;
                if (formHoldsPiece()) commit('setAddOnEditorDeleteError', false);
                try {
                    await dispatch('deleteAddOn', id);
                } catch (error) {
                    Sentry.captureException(error, { tags: { feature: 'add-ons' } });
                    if (formHoldsPiece()) commit('setAddOnEditorDeleteError', true);
                    return false;
                }

                if (formHoldsPiece()) {
                    commit('closeAddOnEditor');
                    commit('setAddOnViewer', null);
                }
                announceToScreenReader(addOnStrings(state.localizations, state.lang).announceDeleted);
                return true;
            },
            busyFlag(context, editor)
        );
    },

    /**
     * The one place between the editor and where pieces live. The host keeps
     * them when there is one, and the store records what it answered, so a
     * new piece takes the host's id. With no host to ask, the piece stays in
     * this store under a local id and is gone on reload. A file goes to the
     * host scaled to the size it keeps.
     * @param {object} record - A draft with or without an id. A new piece
     *   carries the uuid its form was given
     * @returns {Promise<object>} The record as stored
     * @throws {Error} When the record names an id the store does not hold,
     *   the host refuses the save, or the image file cannot be read
     */
    async saveAddOn({ state, commit }, record) {
        const sending = await withHostSizedImage(record);
        if (sending.id == null) {
            const saved = await addOnsAPI.store(sending);
            const kept = saved ? hostRecord(saved) : localRecord(sending);
            commit('addAddOn', kept);

            return findAddOnById(state.articles, kept.id);
        }

        const stored = findAddOnById(state.articles, sending.id);
        // The host answers a 404 for the same request.
        if (!stored) {
            throw new Error(`Unable to save add-on with ID: ${sending.id}. The store does not hold it.`);
        }

        const changes = changedFields(sending, stored);
        if (Object.keys(changes).length === 0) return stored;

        const saved = await addOnsAPI.update(stored.id, changes);
        commit('updateAddOn', { id: stored.id, changes: saved ? hostRecord(saved) : withLocalImage(sending) });

        return findAddOnById(state.articles, stored.id);
    },
    /**
     * @param {string|number} id - The piece to remove
     * @returns {Promise<void>}
     * @throws {Error} When the host refuses it, leaving the piece where it was
     */
    async deleteAddOn({ commit }, id) {
        await addOnsAPI.delete(id);
        commit('removeAddOn', id);
    },
};

/**
 * Ids match as strings, so the record's spelling must not replace the
 * stored one: the list keys its rows by id.
 */
function replaceAddOnAt(state, index, record) {
    state.articles.splice(index, 1, normalizeAddOn({ ...record, id: state.articles[index].id }));
}

export const addOnsMutations = {
    setIssueArticles(state, payload) {
        // The host sends a preview every published piece, including those on
        // the pages it withholds, and a piece carries its page's text.
        if (state.previewMode) {
            state.articles = [];
            return;
        }
        // Every record leaves here in one shape, whichever era the payload is
        // from. An empty entry is dropped here, so nothing downstream ever
        // has to ask whether a record exists.
        state.articles = (payload.articles ?? []).map(normalizeAddOn).filter(Boolean);
    },
    addAddOn(state, record) {
        const index = state.articles.findIndex(addOn => hasAddOnId(addOn, record.id));
        if (index >= 0) {
            replaceAddOnAt(state, index, record);
            return;
        }

        // A copy, because normalizeAddOn returns the record it was given
        // when nothing needs changing, and that record is the editor's
        // own draft object.
        state.articles.push(normalizeAddOn({ ...record }));
    },
    updateAddOn(state, { id, changes }) {
        const index = state.articles.findIndex(addOn => hasAddOnId(addOn, id));
        if (index < 0) return;

        replaceAddOnAt(state, index, { ...state.articles[index], ...changes });
    },
    removeAddOn(state, id) {
        state.articles = state.articles.filter(addOn => !hasAddOnId(addOn, id));
    },
    setSharedArticle(state, payload) {
        state.sharedArticle.id = payload.articleId;
    },
    setInitialPanel(state, panel) {
        state.initialPanel = panel ?? null;
    },
    setAddOnsEditing(state, payload) {
        state.ui.features.addOns.editing = Boolean(payload.enabled);
    },
    setAddOnViewer(state, addOnId) {
        state.ui.addOnsPanel.addOnId = addOnId ?? null;
        // The list has nothing to expand, so leaving a piece leaves fullscreen.
        if (addOnId == null) state.ui.addOnsPanel.fullscreen = false;
    },
    setAddOnViewerFullscreen(state, fullscreen) {
        state.ui.addOnsPanel.fullscreen = Boolean(fullscreen);
    },
    setAddOnHighlight(state, addOnId) {
        state.ui.addOnsPanel.highlightedId = addOnId ?? null;
    },
    setAddOnSearch(state, query) {
        state.ui.addOnsPanel.search = typeof query === 'string' ? query : '';
    },
    setAddOnFontScale(state, scale) {
        state.ui.addOnsPanel.fontScale = nearestScale(scale);
    },
    openAddOnEditor(state, { id, draft, baseline = null, uuid = null }) {
        state.ui.addOnsPanel.editor = {
            id,
            draft,
            // The stored piece in the form's own terms, as far as the form
            // knows the host holds it. A new piece has none.
            baseline,
            // Sent on every try of the first save, so the host stores the piece once.
            uuid,
            // The host may have stored any of these, and it answers the uuid with that one.
            refusedTries: [],
            anchorBeforeCapture: null,
            titleError: false,
            saveError: false,
            deleteError: false,
            imageError: false,
            busy: false,
        };
        // The operator has to see the page to tell where the piece lands
        // and to move its anchor, so the form never covers the reader.
        state.ui.addOnsPanel.fullscreen = false;
        // The selection now belongs to the draft, so the tool that would act
        // on it goes, rather than waiting hidden and coming back on close.
        state.ui.selectionTool.opened = false;
    },
    setAddOnEditorField(state, { key, value }) {
        const editor = state.ui.addOnsPanel.editor;
        if (!editor) return;

        editor.draft[key] = value;
        // A write names one image, so a chosen file and a typed URL each replace the other.
        if (key === 'imageFile' && value) editor.draft.imageUrl = '';
        if (key === 'imageUrl' && value) editor.draft.imageFile = null;
        if (key === 'imageFile' || key === 'imageUrl') editor.imageError = false;
        // Any edit is the operator trying again, so the last failure stops being news.
        editor.saveError = false;
        if (key === 'title' && value.trim()) editor.titleError = false;
    },
    setAddOnEditorType(state, type) {
        const editor = state.ui.addOnsPanel.editor;
        if (!editor) return;

        editor.draft.typeAuto = type === 'auto';
        // Automatic keeps no answer of its own: the getter reads it off the content.
        editor.draft.type = editor.draft.typeAuto ? null : type;
    },
    setAddOnEditorAnchor(state, anchor) {
        const editor = state.ui.addOnsPanel.editor;
        if (!editor) return;

        editor.draft = { ...editor.draft, anchor };
        // An anchor that landed ends the capture, so there is nothing left to
        // come back to: the way out of here is Undo, not Cancel.
        editor.anchorBeforeCapture = null;
    },
    /**
     * Puts the draft's anchor in the state the page answers, keeping the one
     * it replaces. A capture already running keeps what it remembered: the
     * operator switching kinds mid-capture is still choosing, and cancelling
     * owes them the anchor they arrived with.
     */
    startAddOnEditorAnchorCapture(state, anchor) {
        const editor = state.ui.addOnsPanel.editor;
        if (!editor) return;

        if (!isPendingAnchor(editor.draft.anchor)) editor.anchorBeforeCapture = editor.draft.anchor ?? null;
        editor.draft = { ...editor.draft, anchor };
    },
    setAddOnEditorTitleError(state, hasError) {
        if (state.ui.addOnsPanel.editor) state.ui.addOnsPanel.editor.titleError = Boolean(hasError);
    },
    /** @param {boolean|string} error - A flag, or the host's own words about what it refused */
    setAddOnEditorSaveError(state, error) {
        if (!state.ui.addOnsPanel.editor) return;

        state.ui.addOnsPanel.editor.saveError = typeof error === 'string' ? error : Boolean(error);
    },
    setAddOnEditorImageError(state, hasError) {
        if (state.ui.addOnsPanel.editor) state.ui.addOnsPanel.editor.imageError = Boolean(hasError);
    },
    setAddOnEditorDeleteError(state, hasError) {
        if (state.ui.addOnsPanel.editor) state.ui.addOnsPanel.editor.deleteError = Boolean(hasError);
    },
    setAddOnEditorBusy(state, busy) {
        if (state.ui.addOnsPanel.editor) state.ui.addOnsPanel.editor.busy = Boolean(busy);
    },
    /** The copy of the piece the host holds, as one form's saves know it. */
    setAddOnEditorBaseline(_state, { editor, baseline }) {
        editor.baseline = baseline;
    },
    keepAddOnEditorRefusedTry(state, record) {
        if (state.ui.addOnsPanel.editor) state.ui.addOnsPanel.editor.refusedTries.push(record);
    },
    closeAddOnEditor(state) {
        state.ui.addOnsPanel.editor = null;
    },
};
